<template>
  <!-- Who changed what, newest first: hairline rows in the drawer section -->
  <div class="grid gap-2 pt-1">
    <div class="flex items-center justify-between gap-3">
      <p class="text-[13px] text-kb-muted m-0">
        {{ description }}
      </p>
      <Button
        v-tooltip.left="'Refresh'"
        icon="pi pi-refresh"
        size="small"
        text
        severity="secondary"
        aria-label="Refresh history"
        :loading="loading"
        @click="load"
      />
    </div>

    <p
      v-if="error"
      class="kb-callout-alert m-0"
      role="alert"
    >
      {{ error }}
    </p>

    <p
      v-else-if="!loading && rows.length === 0"
      class="text-[13px] text-kb-muted m-0 py-2"
    >
      Nothing recorded yet.
    </p>

    <ul
      v-else
      class="kb-rows list-none m-0 p-0"
    >
      <li
        v-for="row in displayRows"
        :key="row.id"
        class="grid gap-1 py-2"
      >
        <div class="flex items-baseline justify-between gap-3">
          <span class="kb-row-title">{{ row.display.title }}</span>
          <span class="kb-row-meta whitespace-nowrap">{{ formatDateTime(row.entry.at) }}</span>
        </div>
        <p class="m-0 flex flex-wrap items-center gap-1 text-[12.5px] text-kb-muted">
          <span>by {{ row.display.actor }}</span>
          <span
            v-for="role in row.display.actorRoles"
            :key="role"
            class="kb-tag kb-tag-neutral"
          >{{ role }}</span>
        </p>
        <p
          v-if="row.display.fields.length"
          class="m-0 flex flex-wrap gap-1"
        >
          <code
            v-for="field in row.display.fields"
            :key="field"
            class="font-mono text-[11px] px-1.5 py-px rounded-sm border border-kb-line bg-kb-raised text-kb-fg"
          >{{ field }}</code>
        </p>
        <p
          v-if="row.display.detail"
          class="kb-row-hint"
        >
          {{ row.display.detail }}
        </p>
      </li>
    </ul>

    <Button
      v-if="hasMore"
      label="Load older"
      size="small"
      outlined
      severity="secondary"
      class="justify-self-center"
      :loading="loading"
      @click="loadMore"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useAuditLog } from '~/composables/useAuditLog'
import { describeAuditEntry } from '~/utils/auditDisplay'
import { formatDateTime } from '~/utils/dateUtils'

interface Props {
  /** Show the history of this document, e.g. 'subkrewes/abc'. */
  refPath?: string
  /** Or everything this member changed. */
  actorUid?: string
}

const props = defineProps<Props>()

const { rows, loading, error, hasMore, load, loadMore } = useAuditLog(
  props.actorUid ? { actorUid: props.actorUid } : { refPath: props.refPath ?? '' },
)

const description = computed(() => props.actorUid
  ? 'Changes this member made. Values are not recorded, only which fields changed.'
  : 'Who changed what, newest first. Values are not recorded, only which fields changed.')

const displayRows = computed(() => rows.value.map(row => ({
  ...row,
  display: describeAuditEntry(row.entry, props.refPath),
})))

onMounted(load)
</script>
